import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { ChoiceButton } from "@/components/ChoiceButton";
import { Check, Clock3, GitMerge, Search, Undo2 } from "lucide-react";

/* COMPONENTS WAITING FOR APPROVAL (his call, 26 Oct): a value filed under a name that isn't on the list (the AI's
   "Brakes") stays under that name on every truck, marked "new component — waiting for approval", and can be marked
   on "What's still on it?" like any other. Here a list admin approves it onto the list, or merges it into a listed
   component — counted first, then Go, logged, and a merge can be put back. Nothing is deleted. */
const when = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "");
const usage = (s) => `${s.trucks} truck(s) · ${s.values} value(s) · ${s.numbers} number(s) · ${s.ratings} rating(s)`;
// other written forms worth showing — not the row's own name in different capitals
const spelled = (s) => (s.spellings || []).filter((x) => x.toLowerCase() !== (s.name || "").toLowerCase());

function FoundInData({ onDone }) {
  const [scan, setScan] = useState(null);
  const [busy, setBusy] = useState(false);
  const [run, setRun] = useState(null);
  const count = async () => { setBusy(true); try { setScan((await api.get("/component-suggestions/scan")).data); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } setBusy(false); };
  const go = async () => {
    setBusy(true);
    try { const { data } = await api.post("/component-suggestions/scan/go"); setRun(data); toast.success(`${data.registered} name(s) are on the waiting list`); setScan(null); onDone(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setBusy(false);
  };
  const undo = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/component-suggestions/runs/${run.run_id}/undo`); toast.success(`Put back — ${data.withdrawn} taken off the waiting list`); setRun(null); onDone(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setBusy(false);
  };
  return (
    <div data-testid="cs-found" className="rounded-lg border border-slate-200 bg-white p-2.5 space-y-1.5 text-[12px] text-slate-600">
      <div className="flex flex-wrap items-center gap-2">
        <span>Values already filed under names that aren't on the list:</span>
        <ChoiceButton data-testid="cs-scan" disabled={busy} onClick={count}><Search className="w-3.5 h-3.5 mr-1" />Count them</ChoiceButton>
        {run && <ChoiceButton tone="danger" data-testid="cs-scan-undo" disabled={busy} onClick={undo}><Undo2 className="w-3.5 h-3.5 mr-1" />Put back</ChoiceButton>}
      </div>
      {scan && (
        <div data-testid="cs-scan-result" className="space-y-1">
          <p><b data-testid="cs-scan-values">{scan.values}</b> value(s) on <b data-testid="cs-scan-trucks">{scan.trucks}</b> truck(s), under {scan.names.length} name(s): {scan.names.map((n) => `${n.name} (${n.trucks})`).join(", ") || "none"}.</p>
          <p>Go puts the {scan.to_register} name(s) not yet listed on the waiting list below. Nothing on any truck moves.</p>
          <ChoiceButton tone="go" data-testid="cs-scan-go" disabled={busy || !scan.to_register} onClick={go}><Check className="w-3.5 h-3.5 mr-1" />Go</ChoiceButton>
        </div>
      )}
    </div>
  );
}

function useMergePick(s) {
  const [into, setInto] = useState("");
  const [pv, setPv] = useState(null);
  const pick = async (code) => {
    setInto(code); setPv(null);
    if (code) { try { setPv((await api.get(`/component-suggestions/${s.id}/merge-preview`, { params: { into: code } })).data); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } }
  };
  return { into, pv, pick };
}

// SUGGESTED MERGE (his call, 26 Oct): the closest listed component, matched by name and common shop terms, beside the
// waiting name — one tap shows the count, Go merges. Says plainly when nothing on the list is close.
function Closest({ s, i, busy, pick }) {
  const best = s.closest?.[0];
  if (!best) return <span data-testid={`cs-no-close-${i}`} className="text-[11px] text-slate-500">No close match on your list — approve it as a new component</span>;
  return (
    <span data-testid={`cs-suggest-${i}`} className="inline-flex flex-wrap items-center gap-1 text-[11px] text-slate-600">
      Closest:
      <ChoiceButton data-testid={`cs-suggest-pick-${i}`} disabled={busy} className="h-8 min-h-8" onClick={() => pick(best.code)}>
        <GitMerge className="w-3 h-3 mr-1" />{best.name}
      </ChoiceButton>
      {best.why && <span data-testid={`cs-suggest-why-${i}`} className="text-slate-500">{best.why}</span>}
    </span>
  );
}

function MergeStep({ s, i, groups, busy, act, m }) {
  const { into, pv, pick } = m;
  return (
    <div className="flex flex-wrap items-center gap-1.5 mt-1 ml-6 text-[12px] text-slate-500">
      Or merge into:
      {(s.closest || []).slice(1).map((c) => <ChoiceButton key={c.code} data-testid={`cs-closest-${i}-${c.code}`} disabled={busy} onClick={() => pick(c.code)}>{c.name}</ChoiceButton>)}
      <select data-testid={`cs-merge-pick-${i}`} value={into} onChange={(e) => pick(e.target.value)} className="h-9 rounded-lg border-[1.5px] border-slate-300 bg-white px-2 text-xs">
        <option value="">Pick a component on the list…</option>
        {groups.map((g) => <option key={g.code} value={g.code}>{g.name}</option>)}
      </select>
      {pv && (
        <span data-testid={`cs-merge-preview-${i}`} className="w-full text-slate-700">
          Moves {pv.values} value(s), {pv.numbers} number(s) and {pv.ratings} rating(s) on {pv.trucks} truck(s) from “{pv.name}” into “{pv.into}”.
          {pv.both_rated ? ` ${pv.both_rated} truck(s) already have a rating on both — “${pv.into}” keeps its own and the other is kept beside it.` : ""}
          <ChoiceButton tone="go" data-testid={`cs-merge-go-${i}`} disabled={busy} className="ml-2"
            onClick={() => act(`/component-suggestions/${s.id}/merge`, { into }, `“${s.name}” merged into “${pv.into}”`)}>
            <GitMerge className="w-3.5 h-3.5 mr-1" />Go — merge
          </ChoiceButton>
        </span>
      )}
    </div>
  );
}

function WaitingRow({ s, i, groups, busy, act }) {
  const m = useMergePick(s);
  return (
    <div>
      <div data-testid={`cs-row-${i}`} className="flex flex-wrap items-center gap-2 bg-white border border-amber-200 rounded-lg px-3 py-2 text-sm">
        <Clock3 className="w-4 h-4 text-amber-600" />
        <b data-testid={`cs-name-${i}`}>{s.name}</b>
        <Closest s={s} i={i} busy={busy} pick={m.pick} />
        <ChoiceButton tone="go" data-testid={`cs-approve-${i}`} disabled={busy} className="ml-auto"
          onClick={() => act(`/component-suggestions/${s.id}/approve`, null, `“${s.name}” is on the component list — on every truck now`)}>
          <Check className="w-3.5 h-3.5 mr-1" />Approve “{s.name}”
        </ChoiceButton>
        <span data-testid={`cs-usage-${i}`} className="w-full text-[11px] text-slate-500 pl-6">{usage(s)} · {s.source === "ai_read" ? "the AI's read" : s.source === "review" ? "marked on a truck" : "found in the data"} · {when(s.at)}
          {spelled(s).length > 0 && <span data-testid={`cs-spellings-${i}`}> · also written as {spelled(s).map((x) => `“${x}”`).join(", ")} — one component</span>}
          {s.similar_waiting?.length > 0 && <span data-testid={`cs-similar-${i}`}> · also waiting: {s.similar_waiting.join(", ")} — approve one, then merge the other into it</span>}
        </span>
      </div>
      <MergeStep s={s} i={i} groups={groups} busy={busy} act={act} m={m} />
    </div>
  );
}

export function ComponentSuggestionsPanel() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => { api.get("/component-suggestions").then(({ data: d }) => setData(d)).catch(() => setData(null)); }, []);
  useEffect(() => { load(); }, [load]);
  const act = async (path, body, ok) => {
    setBusy(true);
    try { await api.post(path, body); toast.success(ok); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setBusy(false); load();
  };
  if (!data) return null;
  return (
    <section data-testid="cs-panel" className="rounded-xl border-2 border-amber-300 bg-amber-50/50 p-3 space-y-2">
      <h3 className="font-display font-bold text-[#1F1F1F]">Components waiting for approval <span data-testid="cs-count" className="font-mono text-sm text-amber-800">({data.total})</span></h3>
      <p className="text-[12px] text-slate-600">Names a truck carries values under that aren't on your list — the AI's read, or a component archived while trucks still had values under it. Each stays under its own name and can be marked on the truck. Approve it onto the list, or merge it into one you have.</p>
      <FoundInData onDone={load} />
      {data.waiting.map((s, i) => <WaitingRow key={s.id} s={s} i={i} groups={data.groups} busy={busy} act={act} />)}
      {data.merged.length > 0 && (
        <div data-testid="cs-merged" className="pt-1 space-y-1">
          <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Recently merged</div>
          {data.merged.map((s, i) => (
            <div key={s.id} data-testid={`cs-merged-${i}`} className="flex flex-wrap items-center gap-2 text-[13px] bg-white border border-slate-200 rounded-lg px-3 py-1.5">
              <span className="line-through text-[#B01E28]">{s.name}</span> → <b>{s.merged_into_name || s.merged_into}</b>
              <span className="text-[11px] text-slate-500">{s.trucks} truck(s) · by {(s.decided_by || "").split("@")[0]} · {when(s.decided_at)}</span>
              <ChoiceButton tone="danger" data-testid={`cs-merge-undo-${i}`} disabled={busy} className="ml-auto h-8 min-h-8"
                onClick={() => act(`/component-suggestions/${s.id}/undo`, null, `Put back — “${s.name}” is waiting again`)}>
                <Undo2 className="w-3 h-3 mr-1" />Put back
              </ChoiceButton>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
